/**
 * OverviewTab Component
 * AIDLC Administrative Backend
 *
 * Project overview tab showing the agents working on it now, milestones, team, active sprint summary,
 * blockers, and collapsible decisions/risks sections.
 * Uses a 2-column grid layout per Maya's design spec.
 */

import { useState, useEffect } from 'react';
import { Badge } from '../../shared/Badge';
import { BlockerList } from '../blockers/BlockerList';
import { ProjectAgentSessionsPanel } from '../overview/ProjectAgentSessionsPanel';
import { useBlockers } from '../../../hooks/useBlockers';
import { useSprints } from '../../../hooks/useSprints';
import { fetchProjectAgents } from '../../../services/projectManagementService';
import { resolveBlocker } from '../../../services/blockerService';
import type { Project } from '../../../types/project';

interface OverviewTabProps {
  project: Project & { activeSprintId?: string | null; health?: string };
}

const milestoneStatusVariants: Record<string, 'success' | 'warning' | 'neutral'> = {
  completed: 'success',
  'in-progress': 'warning',
  pending: 'neutral',
};

function getAgentLabel(agentId: string): string {
  return agentId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getInitials(agentId: string): string {
  return agentId
    .split('-')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

export function OverviewTab({ project }: OverviewTabProps) {
  const { openBlockers, refresh: refreshBlockers } = useBlockers(project.id);
  const { activeSprint } = useSprints(project.id);
  const [projectAgents, setProjectAgents] = useState<string[]>(project.assignedAgents || []);
  const [showDecisions, setShowDecisions] = useState(false);
  const [showRisks, setShowRisks] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const agents = await fetchProjectAgents(project.id);
        if (!cancelled) setProjectAgents(agents);
      } catch {
        // Fall back to project.assignedAgents
        if (!cancelled) setProjectAgents(project.assignedAgents || []);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [project.id, project.assignedAgents]);

  const handleResolveBlocker = async (blockerId: string) => {
    try {
      await resolveBlocker(blockerId, 'system');
      refreshBlockers();
    } catch (err) {
      console.error('[OverviewTab] Failed to resolve blocker:', err);
    }
  };

  // Calculate sprint progress
  const sprintProgress = activeSprint
    ? {
        total: activeSprint.backlogItemIds.length,
        daysElapsed: Math.floor(
          (Date.now() - new Date(activeSprint.startDate).getTime()) / (1000 * 60 * 60 * 24)
        ),
        daysTotal: Math.floor(
          (new Date(activeSprint.endDate).getTime() - new Date(activeSprint.startDate).getTime()) / (1000 * 60 * 60 * 24)
        ),
      }
    : null;

  const daysRemaining = sprintProgress
    ? Math.max(0, sprintProgress.daysTotal - sprintProgress.daysElapsed)
    : null;

  return (
    <div>
      {/* Project header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{project.name}</h2>
          <Badge variant="info">{project.aidlcState}</Badge>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{project.description}</p>
      </div>

      {/* AGENT-STATUS-1: who is working on this project right now, live */}
      <ProjectAgentSessionsPanel projectId={project.id} />

      {/* 2-column grid: Milestones + Team */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Milestones */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">Milestones</h3>
          {project.milestones.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No milestones defined.</p>
          ) : (
            <div className="space-y-2">
              {project.milestones.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{m.name}</div>
                    <div className="text-[10px] text-gray-400">
                      {m.phase} | Target: {new Date(m.targetDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      {m.completedDate && (
                        <span className="text-green-600 ml-1">
                          (Completed {new Date(m.completedDate).toLocaleDateString([], { month: 'short', day: 'numeric' })})
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge variant={milestoneStatusVariants[m.status] || 'neutral'} dot>
                    {m.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">Team</h3>
          {projectAgents.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No agents assigned.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {projectAgents.map((agentId) => (
                <div key={agentId} className="flex items-center gap-2 bg-gray-50 rounded-full px-3 py-1.5">
                  <div className="w-6 h-6 rounded-full bg-ldaja-blue/10 text-ldaja-blue flex items-center justify-center text-[9px] font-bold">
                    {getInitials(agentId)}
                  </div>
                  <span className="text-xs text-gray-700">{getAgentLabel(agentId)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 2-column grid: Active Sprint + Blockers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Active Sprint */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">Active Sprint</h3>
          {activeSprint ? (
            <div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{activeSprint.name}</div>
              <p className="text-xs text-gray-500 mt-1">{activeSprint.goal || 'No goal set.'}</p>

              {/* Progress bar */}
              {sprintProgress && (
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                    <span>{sprintProgress.total} items in sprint</span>
                    <span>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-ldaja-blue rounded-full transition-all"
                      style={{
                        width: `${sprintProgress.daysTotal > 0 ? Math.min(100, (sprintProgress.daysElapsed / sprintProgress.daysTotal) * 100) : 0}%`,
                      }}
                    />
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {new Date(activeSprint.startDate).toLocaleDateString()} - {new Date(activeSprint.endDate).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No active sprint.</p>
          )}
        </div>

        {/* Blockers */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Blockers</h3>
            {openBlockers.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-700 rounded-full font-medium">
                {openBlockers.length} open
              </span>
            )}
          </div>
          <BlockerList blockers={openBlockers} onResolve={handleResolveBlocker} compact />
        </div>
      </div>

      {/* Collapsible sections: Decisions + Risks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Decisions */}
        <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
          <button
            onClick={() => setShowDecisions(!showDecisions)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-lg"
          >
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">
              Decisions ({project.decisions.length})
            </h3>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${showDecisions ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showDecisions && (
            <div className="px-4 pb-4 space-y-2">
              {project.decisions.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No decisions recorded.</p>
              ) : (
                project.decisions.map((d) => (
                  <div key={d.id} className="border-l-2 border-ldaja-blue pl-3 py-1">
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200">{d.title}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{d.decision}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      By {d.decidedBy} on {new Date(d.decidedAt).toLocaleDateString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Risks */}
        <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
          <button
            onClick={() => setShowRisks(!showRisks)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-lg"
          >
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">
              Risks ({project.risks.length})
            </h3>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${showRisks ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showRisks && (
            <div className="px-4 pb-4 space-y-2">
              {project.risks.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No risks identified.</p>
              ) : (
                project.risks.map((r) => (
                  <div key={r.id} className={`border-l-2 pl-3 py-1 ${
                    r.level === 'high' ? 'border-red-500' :
                    r.level === 'medium' ? 'border-yellow-500' :
                    'border-gray-300'
                  }`}>
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200">{r.description}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      Mitigation: {r.mitigation}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant={r.level === 'high' ? 'danger' : r.level === 'medium' ? 'warning' : 'neutral'}>
                        {r.level}
                      </Badge>
                      <span className={`text-[10px] ${
                        r.status === 'mitigated' ? 'text-green-600' :
                        r.status === 'closed' ? 'text-gray-400' :
                        'text-red-600'
                      }`}>
                        {r.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
